import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import apiClient from '../api/api-client'
import { AxiosResponse } from 'axios'

import { Kennzahl, KennzahlCollection } from '../model'

export const useKennzahlStore = defineStore('kennzahl', () => {
    const kennzahlen = ref<Array<Kennzahl>>([])
    const totalItems = ref<number>(0)
    const loadingArray = ref<Array<string>>([])
    const errorArray = ref({ fetch: false, change: false, create: false })
    const isLoading = computed(() => loadingArray.value.length > 0)
    const hasErrors = computed(
        () => errorArray.value.fetch || errorArray.value.change || errorArray.value.create
    )
    const getErrors = computed(() => errorArray)
    const getKennzahlen = computed<Kennzahl[]>(() =>
        kennzahlen.value
            .slice()
            .sort((a: Kennzahl, b: Kennzahl) =>
                a.studiengangId.localeCompare(b.studiengangId)
            )
    )
    const getKennzahlById = function getKennzahlById(
        kennzahlId: string
    ): Kennzahl | undefined {
        return kennzahlen.value.find(
            (kennzahl) => kennzahl.kennzahlId === kennzahlId
        )
    }

    const removeKennzahlFromState = (kennzahlId: Kennzahl['kennzahlId']): void => {
        const index = kennzahlen.value.findIndex(
            (item: Kennzahl) => item.kennzahlId === kennzahlId
        )

        if (index !== -1) {
            kennzahlen.value.splice(index, 1)
        }
    }

    async function fetchKennzahlen(): Promise<void> {
        loadingArray.value.push('fetch')
        errorArray.value.fetch = false
        kennzahlen.value = []
        const url: string = '/api/kennzahlen'
        try {
            const response: AxiosResponse<KennzahlCollection> = await apiClient.get(url, {
                params: {
                    pagination: false
                }
            })
            kennzahlen.value = response.data['hydra:member']
            totalItems.value = response.data['hydra:totalItems'] ?? 0
        } catch (error: any) {
            errorArray.value.fetch = error.response
        } finally {
            loadingArray.value.splice(loadingArray.value.indexOf('fetch'), 1)
        }
    }

    async function uploadKennzahlenFile(file: string) {
        loadingArray.value.push('uploadKennzahlen')
        errorArray.value.change = false
        try {
            await apiClient.post('/api/statistik/kennzahl-csv-import', {
                fileData: file
            })

            return true
        } catch (error: any) {
            errorArray.value.change = error.response
        }
    }

    async function removeKennzahl(kennzahl: Kennzahl) {
        loadingArray.value.push('removeKennzahl')
        errorArray.value.change = false
        try {
            await apiClient.post('/api/kennzahlen/remove', {
                kennzahl: kennzahl['@id']
            })
            removeKennzahlFromState(kennzahl.kennzahlId)
        } catch (error: any) {
            errorArray.value.change = error.response
        } finally {
            loadingArray.value.splice(loadingArray.value.indexOf('removeKennzahl'), 1)
        }
    }

    return {
        kennzahlen,
        totalItems,
        loadingArray,
        errorArray,
        isLoading,
        hasErrors,
        getErrors,
        getKennzahlen,
        getKennzahlById,
        fetchKennzahlen,
        uploadKennzahlenFile,
        removeKennzahl
    }
})
